import React, { createContext, useContext, useReducer, useEffect } from 'react';

const AuthContext = createContext();

const initialState = {
  user: null,
  users: [],
  roles: [
    {
      id: 'admin',
      name: 'Administrator',
      description: 'Full access to all features and user management',
      permissions: [
        'users.view',
        'users.create',
        'users.edit',
        'users.delete',
        'customers.view',
        'customers.create',
        'customers.edit',
        'customers.delete',
        'leads.view',
        'leads.create',
        'leads.edit',
        'leads.delete',
        'sales.view',
        'sales.create',
        'sales.edit',
        'sales.delete',
        'inventory.view',
        'inventory.create',
        'inventory.edit',
        'inventory.delete',
        'analytics.view',
        'ai-tools.view',
        'settings.view',
        'settings.edit'
      ]
    },
    {
      id: 'manager',
      name: 'Sales Manager',
      description: 'Manage sales team and view all sales data',
      permissions: [
        'users.view',
        'customers.view',
        'customers.create',
        'customers.edit',
        'leads.view',
        'leads.create',
        'leads.edit',
        'sales.view',
        'sales.create',
        'sales.edit',
        'inventory.view',
        'analytics.view',
        'ai-tools.view'
      ]
    },
    {
      id: 'salesperson',
      name: 'Sales Representative',
      description: 'Manage own customers and leads',
      permissions: [
        'customers.view',
        'customers.create',
        'customers.edit',
        'leads.view',
        'leads.create',
        'leads.edit',
        'sales.view',
        'sales.create',
        'inventory.view',
        'ai-tools.view'
      ]
    },
    {
      id: 'viewer',
      name: 'Viewer',
      description: 'Read-only access to basic information',
      permissions: [
        'customers.view',
        'leads.view',
        'sales.view',
        'inventory.view'
      ]
    }
  ],
  loading: false,
  error: null
};

function authReducer(state, action) {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, loading: action.payload };
    case 'SET_ERROR':
      return { ...state, error: action.payload, loading: false };
    case 'SET_USER':
      return { ...state, user: action.payload, loading: false };
    case 'SET_USERS':
      return { ...state, users: action.payload, loading: false };
    case 'ADD_USER':
      return { ...state, users: [action.payload, ...state.users] };
    case 'UPDATE_USER':
      return {
        ...state,
        users: state.users.map(user =>
          user.id === action.payload.id ? action.payload : user
        ),
        user: state.user?.id === action.payload.id ? action.payload : state.user
      };
    case 'DELETE_USER':
      return {
        ...state,
        users: state.users.filter(user => user.id !== action.payload)
      };
    case 'LOGOUT':
      return { ...state, user: null };
    default:
      return state;
  }
}

export function AuthProvider({ children }) {
  const [state, dispatch] = useReducer(authReducer, initialState);

  useEffect(() => {
    loadCurrentUser();
    loadUsers();
  }, []);

  const loadCurrentUser = () => {
    // In a real app, this would check for stored auth token
    const storedUser = localStorage.getItem('autocrm-user');
    if (storedUser) {
      try {
        const user = JSON.parse(storedUser);
        dispatch({ type: 'SET_USER', payload: user });
      } catch (error) {
        console.error('Error parsing stored user:', error);
        localStorage.removeItem('autocrm-user');
      }
    } else {
      // Set default admin user for demo
      const defaultUser = {
        id: 'user-1',
        firstName: 'Admin',
        lastName: 'User',
        email: 'admin@autocrm.com',
        role: 'admin',
        status: 'active',
        avatar: null,
        createdAt: new Date().toISOString(),
        lastLogin: new Date().toISOString()
      };
      dispatch({ type: 'SET_USER', payload: defaultUser });
      localStorage.setItem('autocrm-user', JSON.stringify(defaultUser));
    }
  };

  const loadUsers = () => {
    const storedUsers = localStorage.getItem('autocrm-users');
    if (storedUsers) {
      try {
        const users = JSON.parse(storedUsers);
        dispatch({ type: 'SET_USERS', payload: users });
      } catch (error) {
        console.error('Error parsing stored users:', error);
        dispatch({ type: 'SET_USERS', payload: getSampleUsers() });
      }
    } else {
      const sampleUsers = getSampleUsers();
      dispatch({ type: 'SET_USERS', payload: sampleUsers });
      localStorage.setItem('autocrm-users', JSON.stringify(sampleUsers));
    }
  };

  const getSampleUsers = () => [
    {
      id: 'user-1',
      firstName: 'Admin',
      lastName: 'User',
      email: 'admin@autocrm.com',
      role: 'admin',
      status: 'active',
      avatar: null,
      createdAt: new Date().toISOString(),
      lastLogin: new Date().toISOString()
    },
    {
      id: 'user-2',
      firstName: 'John',
      lastName: 'Manager',
      email: 'john.manager@autocrm.com',
      role: 'manager',
      status: 'active',
      avatar: null,
      createdAt: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
      lastLogin: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString()
    },
    {
      id: 'user-3',
      firstName: 'Sarah',
      lastName: 'Johnson',
      email: 'sarah.johnson@autocrm.com',
      role: 'salesperson',
      status: 'active',
      avatar: null,
      createdAt: new Date(Date.now() - 14 * 24 * 60 * 60 * 1000).toISOString(),
      lastLogin: new Date(Date.now() - 30 * 60 * 1000).toISOString()
    },
    {
      id: 'user-4',
      firstName: 'Mike',
      lastName: 'Davis',
      email: 'mike.davis@autocrm.com',
      role: 'salesperson',
      status: 'active',
      avatar: null,
      createdAt: new Date(Date.now() - 21 * 24 * 60 * 60 * 1000).toISOString(),
      lastLogin: new Date(Date.now() - 4 * 60 * 60 * 1000).toISOString()
    },
    {
      id: 'user-5',
      firstName: 'Emma',
      lastName: 'Wilson',
      email: 'emma.wilson@autocrm.com',
      role: 'viewer',
      status: 'inactive',
      avatar: null,
      createdAt: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
      lastLogin: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString()
    }
  ];

  const login = async (email, password) => {
    dispatch({ type: 'SET_LOADING', payload: true });
    try {
      // In a real app, this would call an authentication API
      const user = state.users.find(u => u.email === email);
      if (user && user.status === 'active') {
        const updatedUser = { ...user, lastLogin: new Date().toISOString() };
        dispatch({ type: 'SET_USER', payload: updatedUser });
        localStorage.setItem('autocrm-user', JSON.stringify(updatedUser));
        return { success: true };
      } else {
        throw new Error('Invalid credentials or inactive account');
      }
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error.message });
      return { success: false, error: error.message };
    }
  };

  const logout = () => {
    dispatch({ type: 'LOGOUT' });
    localStorage.removeItem('autocrm-user');
  };

  const addUser = async (userData) => {
    try {
      const newUser = {
        ...userData,
        id: `user-${Date.now()}`,
        createdAt: new Date().toISOString(),
        lastLogin: null,
        status: 'active'
      };
      
      dispatch({ type: 'ADD_USER', payload: newUser });
      
      const updatedUsers = [newUser, ...state.users];
      localStorage.setItem('autocrm-users', JSON.stringify(updatedUsers));
      
      return newUser;
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  };

  const updateUser = async (id, userData) => {
    try {
      const updatedUser = {
        ...userData,
        id,
        updatedAt: new Date().toISOString()
      };
      
      dispatch({ type: 'UPDATE_USER', payload: updatedUser });
      
      const updatedUsers = state.users.map(user =>
        user.id === id ? updatedUser : user
      );
      localStorage.setItem('autocrm-users', JSON.stringify(updatedUsers));
      
      // Update stored current user if it's the same user
      if (state.user?.id === id) {
        localStorage.setItem('autocrm-user', JSON.stringify(updatedUser));
      }
      
      return updatedUser;
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  };

  const deleteUser = async (id) => {
    try {
      if (id === state.user?.id) {
        throw new Error('Cannot delete your own account');
      }
      
      dispatch({ type: 'DELETE_USER', payload: id });
      
      const updatedUsers = state.users.filter(user => user.id !== id);
      localStorage.setItem('autocrm-users', JSON.stringify(updatedUsers));
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  };

  const hasPermission = (permission) => {
    if (!state.user) return false;
    const userRole = state.roles.find(role => role.id === state.user.role);
    return userRole?.permissions.includes(permission) || false;
  };

  const hasAnyPermission = (permissions) => {
    return permissions.some(permission => hasPermission(permission));
  };

  const getRoleById = (roleId) => {
    return state.roles.find(role => role.id === roleId);
  };

  const contextValue = {
    ...state,
    login,
    logout,
    addUser,
    updateUser,
    deleteUser,
    hasPermission,
    hasAnyPermission,
    getRoleById
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}